import { User } from "../../model/user/User";
import { ChangePasswordDto } from "../../model/user/dto/ChangePasswordDto";
import { CodeVerifyDto } from "../../model/user/dto/CodeVerifyDto";
import { CreateLoginWithGoogleLinkDto } from "../../model/user/dto/CreateLoginWithGoogleLink";
import { ForgotPasswordEmailDto } from "../../model/user/dto/ForgotPasswordEmailDto";
import { ForgotPasswordNewPasswordDto } from "../../model/user/dto/ForgotPasswordNewPasswordDto";
import { ForgotPasswordTokenDto } from "../../model/user/dto/ForgotPasswordTokenDto";
import { PSAGetLastUnreadDto } from "../../model/user/dto/PSAGetLastUnreadDto";
import { LoginDto } from "../../model/user/dto/LoginDto";
import { LoginResponseDto } from "../../model/user/dto/LoginResponseDto";
import { Login2faResendDto } from "../../model/user/dto/Login2faResendDto";
import { LoginResult } from "../../model/user/dto/LoginResult";
import { Verify2faDto } from "../../model/user/dto/Verify2faDto";
import { Verify2faResponseDto } from "../../model/user/dto/Verify2faResponseDto";
import { LoginWithGoogleDto } from "../../model/user/dto/LoginWithGoogleDto";
import { SignupDto } from "../../model/user/dto/SignupDto";
import { SignupSuccessDto } from "../../model/user/dto/SignupSuccessDto";
import { UpdateUserDto } from "../../model/user/dto/UpdateUserDto";
import { AtiraApi } from "../AxiosClient";
import { PSAGetLastUnreadResponseDto } from "../../model/user/dto/PSAGetLastUnreadResponseDto";
import { PSAMarkAsReadDto } from "../../model/user/dto/PSAMarkAsReadDto";
import isString from "lodash/isString";
import { ResendTokenDto } from "../../model/user/dto/ResendTokenDto";
import { ManageAccountAddDto } from "../../model/user/dto/ManageAccountAddDto";
import { ManageAccountRemoveDto } from "../../model/user/dto/ManageAccountRemoveDto";
import { ManageAccountsSwitchDto } from "../../model/user/dto/ManageAccountsSwitchDto";
import { SetPasswordDto } from "../../model/user/dto/SetPasswordDto";

export class UserAxios {
  static login(dto: LoginDto) {
    return AtiraApi.post<LoginResult>(`/auth/login`, dto).then(
      ({ data }) => data,
    );
  }

  static verifyLogin2fa(dto: Verify2faDto) {
    return AtiraApi.post<Verify2faResponseDto>(
      `/auth/login/verify-2fa`,
      dto,
    ).then(({ data }) => data);
  }

  static resendLogin2faCode(dto: Login2faResendDto) {
    return AtiraApi.post<void>(`/auth/login/2fa/resend-code`, dto).then(
      ({ data }) => data,
    );
  }

  static forgotPasswordEmail(dto: ForgotPasswordEmailDto) {
    return AtiraApi.post<void | string>(
      "/auth/forgot-password/email",
      dto,
    ).then(({ data }) => data);
  }

  static forgotPasswordToken(dto: ForgotPasswordTokenDto) {
    return AtiraApi.post<void>("/auth/forgot-password/token", dto).then(
      ({ data }) => data,
    );
  }

  static forgotPasswordNewPassword(dto: ForgotPasswordNewPasswordDto) {
    return AtiraApi.post<void>("/auth/forgot-password/new", dto).then(
      ({ data }) => data,
    );
  }

  static signup(dto: SignupDto) {
    return AtiraApi.post(`/auth/signup`, dto).then(({ data }) => data);
  }

  static verifyUserAccount(dto: CodeVerifyDto) {
    return AtiraApi.post<SignupSuccessDto>(`/auth/code-verify`, dto).then(
      ({ data }) => data,
    );
  }

  static loginWithGoogle(dto: LoginWithGoogleDto) {
    return AtiraApi.post<SignupSuccessDto>(`/auth/google`, dto).then(
      ({ data }) => data,
    );
  }

  static getLoggedInUser(userId: string) {
    return AtiraApi.get<User>(`/user/${userId}`).then(({ data }) => data);
  }

  static createLoginWithGoogleLink(dto: CreateLoginWithGoogleLinkDto) {
    return AtiraApi.post(`/auth/create-google-auth-link`, dto).then(
      ({ data }) => data,
    );
  }

  static updateUserInfo(dto: UpdateUserDto) {
    const formData = new FormData();

    Object.entries(dto).forEach(([key, value]) => {
      if (value instanceof File || value === "null") {
        formData.append(key, value);
      } else {
        formData.append(key, isString(value) ? value : JSON.stringify(value));
      }
    });

    return AtiraApi.patch(`/user/${dto.userId}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(({ data }) => data);
  }

  static updateUserPassword(dto: ChangePasswordDto) {
    return AtiraApi.patch(`/auth/change-password`, dto).then(
      ({ data }) => data,
    );
  }

  static setPassword(dto: SetPasswordDto) {
    return AtiraApi.post(`/auth/google/set-password`, dto).then(
      ({ data }) => data,
    );
  }

  static psaGetLastUnread(dto: PSAGetLastUnreadDto) {
    return AtiraApi.post<PSAGetLastUnreadResponseDto>(
      `/user/${dto.userId}/psa/last-unread`,
      dto,
    ).then(({ data }) => data);
  }

  static psaMarkAsRead(dto: PSAMarkAsReadDto) {
    return AtiraApi.post<void>(
      `/user/${dto.userId}/psa/${dto.psaId}/mark-as-read`,
    ).then(({ data }) => data);
  }

  static resendSignupToken(dto: ResendTokenDto) {
    return AtiraApi.post<void>(`/auth/signup/resend-code`, dto).then(
      ({ data }) => data,
    );
  }

  static resendForgotPasswordToken(dto: ResendTokenDto) {
    return AtiraApi.post<void>(`/auth/forgot-password/resend-code`, dto).then(
      ({ data }) => data,
    );
  }

  static manageAccountsAdd(dto: ManageAccountAddDto) {
    return AtiraApi.post<void>(`/auth/manage-accounts/add`, dto).then(
      ({ data }) => data,
    );
  }

  static manageAccountsRemove(dto: ManageAccountRemoveDto) {
    return AtiraApi.delete<void>(`/auth/manage-accounts/remove`, {
      data: dto,
    }).then(({ data }) => data);
  }

  static manageAccountsSwitch(dto: ManageAccountsSwitchDto) {
    return AtiraApi.post<LoginResponseDto>(
      `/auth/manage-accounts/switch`,
      dto,
    ).then(({ data }) => data);
  }
}
